import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import {
  acceptKvkk,
  getCorporateEmployeeById,
  getCorporateEmployees,
  getCostCenterOptions,
  getPolicyOptions,
} from '../../../../api/endpoints/corporateEmployee';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { EmployeeFormDialog } from '../components/EmployeeFormDialog';
import { IdCardSection } from '../components/IdCardSection';
import { LoyaltySection } from '../components/LoyaltySection';
import { ManagerAssignDialog } from '../components/ManagerAssignDialog';
import { PassportSection } from '../components/PassportSection';

/** Personel detay: özet bilgiler + yönetici ataması + KVKK durumu + Kimlik Kartı / Pasaport
 *  Belgesi sub-formları. */
export default function EmployeeDetailPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const id = Number(employeeId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [managerDialogOpen, setManagerDialogOpen] = useState(false);
  const [kvkkConfirmOpen, setKvkkConfirmOpen] = useState(false);

  const query = useQuery({
    queryKey: ['corporateEmployee', 'byId', id],
    queryFn: () => getCorporateEmployeeById(id),
    enabled: Number.isFinite(id) && id > 0,
  });
  // Reuses the list page's cache keys so these dropdowns don't fire extra requests when
  // navigating here from the list page.
  const employeesQuery = useQuery({ queryKey: ['corporateEmployee', 'list'], queryFn: () => getCorporateEmployees() });
  const policiesQuery = useQuery({ queryKey: ['corporateEmployee', 'policyOptions'], queryFn: () => getPolicyOptions() });
  const costCentersQuery = useQuery({
    queryKey: ['corporateEmployee', 'costCenterOptions'],
    queryFn: () => getCostCenterOptions(),
  });

  const employee = query.data?.corporateEmployeeList?.[0] ?? null;
  const candidates = employeesQuery.data?.corporateEmployeeList ?? [];
  const policies = policiesQuery.data?.policies ?? [];
  const costCenters = costCentersQuery.data?.costCenter ?? [];

  const kvkkMutation = useMutation({
    mutationFn: () => acceptKvkk({ kvkkAccepted: true, kvkkVersion: '1.0' }),
    onSuccess: () => {
      enqueueSnackbar('KVKK onayı kaydedildi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'byId', id] });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'list'] });
      setKvkkConfirmOpen(false);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
        <IconButton onClick={() => navigate('/corporate/employees')} aria-label="Geri">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Personel Detayı</Typography>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      {employee && (
        <Card sx={{ mb: 3 }} variant="outlined">
          <CardContent>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="h6">
                  {employee.name} {employee.surName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {employee.emailAdress || '—'} · {employee.phoneNumber || '—'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                  Düzenle
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SupervisorAccountIcon />}
                  onClick={() => setManagerDialogOpen(true)}
                >
                  Yönetici Ata
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Sicil No
                </Typography>
                <Typography variant="body2">{employee.registrationNumber || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Politika
                </Typography>
                <Typography variant="body2">{employee.policyName || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Masraf Merkezi
                </Typography>
                <Typography variant="body2">{employee.costCenterName || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Yönetici
                </Typography>
                <Typography variant="body2">{employee.managerName || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Durum
                </Typography>
                <Box>
                  <Chip size="small" color={employee.isActive ? 'success' : 'default'} label={employee.isActive ? 'Aktif' : 'Pasif'} />
                </Box>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Rol
                </Typography>
                <Typography variant="body2">
                  {employee.isProgramUser
                    ? 'Satın Alma Yapabilir'
                    : employee.isSp
                    ? 'Seyahat Planlamacısı'
                    : '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  KVKK Durumu
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    color={employee.kvkkAccepted ? 'success' : 'warning'}
                    label={
                      employee.kvkkAccepted
                        ? `Onaylı${employee.kvkkAcceptedAt ? ` (${employee.kvkkAcceptedAt.slice(0, 10)})` : ''}`
                        : 'Onay Bekliyor'
                    }
                  />
                </Box>
              </Grid>
              {!employee.kvkkAccepted && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Tooltip title="Yalnızca oturum sahibinin kendi bağlı personel kaydı için çalışır (gerçek API'de EmployeeID alanı yoktur).">
                    <span>
                      <Button size="small" onClick={() => setKvkkConfirmOpen(true)}>
                        KVKK Onayını Kaydet
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {employee && (
        <Box>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Kimlik Kartları" />
            <Tab label="Pasaport Belgeleri" />
            <Tab label="Sadakat Programları" />
          </Tabs>
          {tab === 0 && <IdCardSection employeeID={employee.employeeID} />}
          {tab === 1 && <PassportSection employeeID={employee.employeeID} />}
          {tab === 2 && <LoyaltySection employeeID={employee.employeeID} />}
        </Box>
      )}

      <EmployeeFormDialog
        open={editOpen}
        employee={employee}
        policies={policies}
        costCenters={costCenters}
        onClose={() => setEditOpen(false)}
      />
      <ManagerAssignDialog
        open={managerDialogOpen}
        employee={employee}
        candidates={candidates}
        onClose={() => setManagerDialogOpen(false)}
      />

      <ConfirmDialog
        open={kvkkConfirmOpen}
        title="KVKK onayını kaydet"
        description="Bu işlem yalnızca oturum sahibinin kendi bağlı personel kaydı için çalışır. Devam etmek istiyor musunuz?"
        confirmLabel="Onayla"
        loading={kvkkMutation.isPending}
        onConfirm={() => kvkkMutation.mutate()}
        onCancel={() => setKvkkConfirmOpen(false)}
      />
    </Box>
  );
}
